import { t } from "../i18n/runtime";
import { useGameStore } from "../stores/gameStore";
import { aiModeLabel, translateAiModeNote } from "../i18n";
import { Pill, formatClock } from "./primitives";
import type { Tone } from "./primitives";
import { roundSecondsRemaining } from "./roundClock";
import { TimeControls } from "./TimeControls";

/**
 * The two things that are true of the session rather than of anything on the
 * board: how much simulated trading time remains, and where the nations are being played from.
 *
 * They used to sit in a bar across the top, which spent 58px of height and a
 * full-width panel on two figures — and put them at the opposite end of the
 * screen from the map they describe. Over the map instead, in the corner, with
 * no chrome of their own.
 *
 * The mode pill doubles as the connection light. A client that has lost the
 * socket goes on drawing the last world it received, so the one place that must
 * say otherwise is the one place the player is already looking for the clock.
 *
 * The pill reports the effective server mode. A credentialed live or hybrid
 * session reaches Codex from its first scheduled turn; a server-side downgrade
 * is carried in aiModeNote instead of guessed in the browser.
 */

/** A replay must not be tinted like a live run, so it keeps its own tone. */
const MODE_TONE: Record<string, Tone> = {
  live: "green",
  hybrid: "green",
  record: "green",
  replay: "violet",
};

export function HudCorner() {
  const roundClock = useGameStore((s) => s.roundClock);
  const ended = useGameStore((s) => s.state?.phase === "ENDED");
  const tickRateHz = useGameStore((s) => s.state?.tickRateHz ?? 0);
  const aiMode = useGameStore((s) => s.aiMode);
  const connected = useGameStore((s) => s.connected);
  const aiModeNote = useGameStore((s) => s.aiModeNote);
  const seconds = roundSecondsRemaining(roundClock, tickRateHz, ended);

  const mode = aiModeLabel(aiMode);
  const text = connected ? mode.text : t("HudCorner.disconnected");
  const tone: Tone = connected
    ? Object.hasOwn(MODE_TONE, aiMode)
      ? MODE_TONE[aiMode]
      : "amber"
    : "red";
  const title = !connected
    ? t("HudCorner.connection_to_the_server_was_lost")
    : aiModeNote
      ? `${mode.title} ${translateAiModeNote(aiModeNote)}.`
      : mode.title;

  return (
    <div className="hud-corner">
      <div className="hud-corner__status">
        <div
          className="hud-corner__clock"
          title={t("HudCorner.game_time_remaining_until_the_round_ends")}
        >
          {formatClock(seconds)}
        </div>
        <Pill tone={tone} title={title}>
          {text}
        </Pill>
      </div>
      <TimeControls />
    </div>
  );
}
